/**
 * Client activity audit (LogClientActivity). Use trackActivity() for significant user actions.
 * @see docs/client-activity-tracking.md — includes guidance for AI agents adding tracking.
 */
import { watch } from "vue";
import type { RouteLocationNormalized } from "vue-router";
import { getEnvConfig } from "~/services/appConfig";
import { authStore } from "~/stores/authStore";
import { AuthState as AuthStateEnum } from "~/types/auth";

const SESSION_STORAGE_KEY = "havn.activitySessionId";
const SESSION_ID_HEADER = "X-Activity-Session-Id";
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE_SIZE = 20;

export type ClientActivityEventType =
  | "PageView"
  | "Login"
  | "Logout"
  | "Activity";

export interface ClientActivityEvent {
  eventType: ClientActivityEventType;
  route?: string;
  routeName?: string | symbol | null;
  entityType?: string;
  entityGuid?: string;
  detail?: string;
  metadata?: string;
  clientTimeStamp?: string;
}

interface QueuedClientActivityEvent {
  eventType: ClientActivityEventType;
  route?: string;
  routeName?: string;
  entityType?: string;
  entityGuid?: string;
  detail?: string;
  metadata?: string;
  sessionId?: string;
  clientTimeStamp?: string;
  sysOrgGuid?: string;
}

let initialized = false;
let flushInFlight: Promise<void> | null = null;
const queue: QueuedClientActivityEvent[] = [];
let lastTrackedPath: string | undefined;
let sessionEndLogged = false;
let serverTimeOffsetMs = 0;

/** Set clock offset from a fresh GetMyUserInfo serverTimeUtc value. */
export function setServerTimeOffset(serverTimeUtc: string): void {
  const serverMs = new Date(serverTimeUtc).getTime();
  if (Number.isNaN(serverMs)) {
    return;
  }

  serverTimeOffsetMs = serverMs - Date.now();
}

export function resetServerTimeOffset(): void {
  serverTimeOffsetMs = 0;
}

function getTrackingTimestamp(): string {
  return new Date(Date.now() + serverTimeOffsetMs).toISOString();
}

function isActivityTrackingSuppressed(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin parent; treat as embedded iframe.
    return true;
  }
}

function getCurrentSysOrgGuid(): string | undefined {
  return sessionStorage.getItem("currentOrgGuid") ?? undefined;
}

function getSessionId(): string {
  const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const sessionId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replaceAll("-", "").slice(0, 32)
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

  sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  return sessionId;
}

function normalizeRouteName(
  routeName: string | symbol | null | undefined,
): string | undefined {
  if (routeName === null || routeName === undefined) {
    return undefined;
  }

  return typeof routeName === "symbol"
    ? routeName.description
    : String(routeName);
}

function routeAllowsAnonymousTracking(to: RouteLocationNormalized): boolean {
  return to.matched.some((record) => record.meta.anonymous === true);
}

function canTrackWithoutAuthentication(event: ClientActivityEvent): boolean {
  if (!getCurrentSysOrgGuid()) {
    return false;
  }

  if (event.eventType === "PageView") {
    return true;
  }

  return event.eventType === "Activity" && event.detail?.trim() === "Ran Calc";
}

function canSendWithoutAuthentication(
  event: QueuedClientActivityEvent,
): boolean {
  if (event.eventType === "PageView") {
    return true;
  }

  return event.eventType === "Activity" && event.detail?.trim() === "Ran Calc";
}

function canEnqueue(event: ClientActivityEvent): boolean {
  if (isActivityTrackingSuppressed()) {
    return false;
  }

  if (authStore.isAuthenticated) {
    return true;
  }

  return canTrackWithoutAuthentication(event);
}

function shouldTrackRoute(to: RouteLocationNormalized): boolean {
  if (to.meta.skipActivityLog === true) {
    return false;
  }

  if (to.name === "NotFound") {
    return false;
  }

  const path = to.fullPath;
  if (!path || path.startsWith("/assets/")) {
    return false;
  }

  if (authStore.isAuthenticated) {
    return true;
  }

  return routeAllowsAnonymousTracking(to) && !!getCurrentSysOrgGuid();
}

function getCurrentRoute(): string {
  return window.location.hash.replace(/^#/, "") || "/";
}

function buildQueuedEvent(
  event: ClientActivityEvent,
): QueuedClientActivityEvent {
  return {
    eventType: event.eventType,
    route: event.route,
    routeName: normalizeRouteName(event.routeName),
    entityType: event.entityType,
    entityGuid: event.entityGuid,
    detail: event.detail,
    metadata: event.metadata,
    sessionId: getSessionId(),
    clientTimeStamp: event.clientTimeStamp ?? getTrackingTimestamp(),
    sysOrgGuid: getCurrentSysOrgGuid(),
  };
}

function enqueue(event: ClientActivityEvent, flushImmediately = false): void {
  if (!canEnqueue(event)) {
    return;
  }

  queue.push(buildQueuedEvent(event));

  if (flushImmediately || queue.length >= MAX_QUEUE_SIZE) {
    void flush(true);
  }
}

async function enqueueAndFlush(event: ClientActivityEvent): Promise<void> {
  if (!canEnqueue(event)) {
    return;
  }

  queue.push(buildQueuedEvent(event));
  await flush(true);
}

function trackBrowserClose(): void {
  if (
    sessionEndLogged ||
    isActivityTrackingSuppressed() ||
    !authStore.isAuthenticated
  ) {
    return;
  }

  sessionEndLogged = true;
  queue.push(
    buildQueuedEvent({
      eventType: "Activity",
      route: getCurrentRoute(),
      detail: "Browser or tab closed",
    }),
  );
  void flush(true);
}

function requeueFailedEvents(events: QueuedClientActivityEvent[]): void {
  const combined = [...events, ...queue];
  queue.splice(0, queue.length, ...combined.slice(0, MAX_QUEUE_SIZE));
}

async function doFlush(useKeepalive = false): Promise<void> {
  if (queue.length === 0) {
    return;
  }

  const events = queue.splice(0, queue.length);
  const sessionId = getSessionId();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    [SESSION_ID_HEADER]: sessionId,
  };

  const token = authStore.isAuthenticated
    ? await authStore.getAccessTokenAsync()
    : null;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else if (!events.every(canSendWithoutAuthentication)) {
    requeueFailedEvents(events);
    return;
  }

  const url = `${getEnvConfig().serverUrl}/api/logging/LogClientActivity/Track`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({
        sysOrgGuid: getCurrentSysOrgGuid(),
        events,
      }),
      keepalive: useKeepalive,
    });

    if (!response.ok) {
      requeueFailedEvents(events);
    }
  } catch {
    requeueFailedEvents(events);
  }
}

async function flush(useKeepalive = false): Promise<void> {
  if (flushInFlight) {
    return flushInFlight;
  }

  flushInFlight = doFlush(useKeepalive).finally(() => {
    flushInFlight = null;
  });
  return flushInFlight;
}

export function trackPageView(to: RouteLocationNormalized): void {
  if (!shouldTrackRoute(to)) {
    return;
  }

  const path = to.fullPath;
  if (path === lastTrackedPath) {
    return;
  }

  lastTrackedPath = path;
  enqueue({
    eventType: "PageView",
    route: path,
    routeName: to.name,
  });
}

/** Call while the user is still authenticated (e.g. at the start of logout). */
export async function trackLogout(): Promise<void> {
  if (
    sessionEndLogged ||
    isActivityTrackingSuppressed() ||
    !authStore.isAuthenticated
  ) {
    return;
  }

  sessionEndLogged = true;
  await enqueueAndFlush({
    eventType: "Logout",
    route: getCurrentRoute(),
    detail: "User signed out",
  });
}

export function trackActivity(
  detail: string,
  options?: {
    route?: string;
    routeName?: string | symbol | null;
    entityType?: string;
    entityGuid?: string;
    metadata?: Record<string, unknown>;
  },
): void {
  enqueue({
    eventType: "Activity",
    route: options?.route,
    routeName: options?.routeName,
    entityType: options?.entityType,
    entityGuid: options?.entityGuid,
    detail,
    metadata: options?.metadata ? JSON.stringify(options.metadata) : undefined,
  });
}

export function initializeActivityTracking(): void {
  if (initialized || isActivityTrackingSuppressed()) {
    return;
  }

  initialized = true;

  setInterval(() => {
    void flush();
  }, FLUSH_INTERVAL_MS);

  window.addEventListener("pagehide", (event) => {
    if (event.persisted) {
      return;
    }

    trackBrowserClose();
    void flush(true);
  });

  window.addEventListener("beforeunload", () => {
    trackBrowserClose();
    void flush(true);
  });

  watch(
    () => authStore.state,
    (newState, oldState) => {
      if (
        newState === AuthStateEnum.AUTHENTICATED &&
        oldState !== AuthStateEnum.AUTHENTICATED
      ) {
        sessionEndLogged = false;
        enqueue(
          {
            eventType: "Login",
            route: getCurrentRoute(),
            detail: "User session established",
          },
          true,
        );
      }
    },
  );
}
